import { useState } from 'react';
import { Coins, BookOpen, Scale, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAMS_IMAGE = "https://images.unsplash.com/photo-1706841533834-3e27f3279e65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxraWRzJTIwcGxheWluZyUyMGJhc2tldGJhbGwlMjB5b3V0aCUyMHNwb3J0c3xlbnwwfHx8fDE3NzQ5ODc4NTN8MA&ixlib=rb-4.1.0&q=85";

export const Programs = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/interest`, { email });
      setStatus({ type: 'success', message: 'Thank you! You\'ve been added to our interest list.' });
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        setStatus({ type: 'error', message: 'This email is already subscribed.' });
      } else {
        setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="programs-page">
      {/* Overview Section */}
      <section className="py-20 md:py-32" data-testid="programs-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
                Our Programs
              </p>
              <h1 className="text-4xl sm:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight mb-8">
                Turning purpose into action
              </h1>
              <p className="text-lg leading-relaxed text-zinc-600">
                We invest in programs and partnerships that help make youth sports safer, more inclusive, and more accessible.
              </p>
            </div>
            <div className="relative">
              <img 
                src={PROGRAMS_IMAGE} 
                alt="Kids playing basketball"
                className="rounded-3xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="programs-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Micro-Grant Program */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="program-grants">
              <div className="w-14 h-14 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mb-6">
                <Coins className="h-7 w-7 text-[#5B4FF9]" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Community Grants</h3>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Our grant program supports organizations working to remove barriers to participation and improve athlete safety.
              </p>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-zinc-700">Funding may support:</p>
                <ul className="space-y-2 text-sm text-zinc-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-[#5B4FF9] mr-2 mt-0.5 flex-shrink-0" />
                    Registration fees
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-[#5B4FF9] mr-2 mt-0.5 flex-shrink-0" />
                    Equipment and uniforms
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-[#5B4FF9] mr-2 mt-0.5 flex-shrink-0" />
                    Travel and facility costs
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-[#5B4FF9] mr-2 mt-0.5 flex-shrink-0" />
                    Safety training and resources
                  </li>
                </ul>
              </div>
            </div>

            {/* Safety Education */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="program-safety">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-6">
                <BookOpen className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Safety Education & Resources</h3>
              <p className="text-zinc-600 leading-relaxed">
                We are building a library of tools and resources to help coaches, parents, and organizations create safer environments for athletes.
              </p>
            </div>

            {/* Advocacy */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="program-advocacy">
              <div className="w-14 h-14 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mb-6">
                <Scale className="h-7 w-7 text-[#5B4FF9]" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Advocacy & Policy</h3>
              <p className="text-zinc-600 leading-relaxed">
                We support efforts to strengthen safety standards in youth sports through collaboration, awareness, and policy initiatives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Grant Application Section */}
      <section className="py-20 md:py-32 bg-[#5B4FF9]" data-testid="grant-application">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white leading-snug mb-6">
              Apply for Funding
            </h2>
            <p className="text-lg text-[#E5E3FF] mb-8">
              We are preparing to launch our first round of community grants. Join our interest list to be notified when applications open.
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white border-0 rounded-full px-6 py-3 text-[#272926] placeholder:text-zinc-400"
                data-testid="interest-email-input"
                required
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#272926] text-white hover:bg-[#1a1a18] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1"
                data-testid="interest-submit-btn"
              >
                {loading ? 'Joining...' : 'Join Interest List'}
              </Button>
            </form>

            {status.message && (
              <div 
                className={`mt-6 flex items-center justify-center gap-2 ${
                  status.type === 'success' ? 'text-green-300' : 'text-red-300'
                }`}
                data-testid="interest-status"
              >
                {status.type === 'success' ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <AlertCircle className="h-5 w-5" />
                )}
                <span>{status.message}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
